/**
 * This is a re-usable component for rendering Assignnment data in an editable
 * way.  It is intended to be used for creation and edits
 **/
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {Id, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {
    type Assignment,
    type StudentList,
    type UserHasChildAssignmentClassrooms,
} from "@khan/assignments-types";
import {
    containsAIGuideActivityContent,
    getTotalStudentCount,
} from "@khan/assignments-utils";
import {mediaQueries} from "@khan/shared-styles";

import ClassSelector from "./class-selector.tsx";
import StudentSelector from "./student-selector.tsx";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";

interface Props {
    saving: boolean;
    studentLists: Array<StudentList>;
    selectedClassDescriptors: Array<string>;
    assignment: Assignment;
    qualifiedContentDescriptorsList: Array<string>;
    dropdownBodyWidth: number;
    userHasChildAssignmentsClassrooms: UserHasChildAssignmentClassrooms;
    handleClassesChange: (classDescriptors: Array<string>) => void;
    handleStudentsChange: (studentIds: Array<string>) => void;
}

export const AssignmentFormClassesAndStudents = ({
    saving,
    studentLists,
    selectedClassDescriptors,
    assignment,
    qualifiedContentDescriptorsList,
    dropdownBodyWidth,
    userHasChildAssignmentsClassrooms,
    handleClassesChange,
    handleStudentsChange,
}: Props) => {
    const {studentList, students} = assignment;
    const totalStudentCount = getTotalStudentCount(
        studentLists,
        selectedClassDescriptors,
    );
    const classDescriptor = studentList ? studentList.descriptor : "";
    const studentIds = (students || []).map((s) => s.kaid);
    const hasMultipleClasses = studentLists.length > 1;
    const hasAIGuideActivityContent =
        qualifiedContentDescriptorsList &&
        containsAIGuideActivityContent(qualifiedContentDescriptorsList);

    const isChildAssignmentsClassroom =
        !!studentList?.isChildAssignmentsClassroom;

    const classLabel = () => {
        // user has one or many classrooms
        // user classrooms are ChildAssignmentsClassrooms
        if (userHasChildAssignmentsClassrooms === "ALL") {
            return t`Children`;
        }

        // user has many classrooms &&
        // at least one ChildAssigmentsClassroom
        if (userHasChildAssignmentsClassrooms === "SOME") {
            return t`Class and Children`;
        }

        // user only no ChildAssignmentsClassrooms
        return t`Class`;
    };

    const studentLabel =
        userHasChildAssignmentsClassrooms === "SOME"
            ? t`Students and Children`
            : t`Students`;

    const shouldRenderStudentSelector =
        !isChildAssignmentsClassroom &&
        userHasChildAssignmentsClassrooms !== "ALL";

    return (
        <View style={styles.container}>
            {(hasMultipleClasses ||
                userHasChildAssignmentsClassrooms === "ALL") && (
                <React.Fragment>
                    <Id>
                        {(classSelectorId) => (
                            <LabeledSelector
                                labelText={classLabel()}
                                selectorId={classSelectorId}
                                selector={
                                    <ClassSelector
                                        disabled={!!assignment.id || saving}
                                        dropdownWidth={dropdownBodyWidth}
                                        onChange={handleClassesChange}
                                        selectedValues={
                                            selectedClassDescriptors
                                        }
                                        id={classSelectorId}
                                        hasChildAssignmentsClassrooms={
                                            userHasChildAssignmentsClassrooms
                                        }
                                        studentListsOrCoachedClassrooms={
                                            studentLists
                                        }
                                    />
                                }
                            />
                        )}
                    </Id>
                    <View style={styles.spacer}>
                        <Strut size={spacing.large_24} />
                    </View>
                </React.Fragment>
            )}

            {shouldRenderStudentSelector && (
                <Id>
                    {(studentSelectorId) => (
                        <LabeledSelector
                            labelText={studentLabel}
                            labelTooltipText={
                                hasAIGuideActivityContent
                                    ? t`Students who do not have Khanmigo enabled cannot be assigned a Khanmigo activity.`
                                    : ""
                            }
                            selectorId={studentSelectorId}
                            selector={
                                <StudentSelector
                                    id={studentSelectorId}
                                    dropdownWidth={dropdownBodyWidth}
                                    onChange={handleStudentsChange}
                                    selectedStudentIds={studentIds}
                                    classDescriptor={classDescriptor}
                                    totalStudentCount={totalStudentCount}
                                    classSelected={
                                        selectedClassDescriptors.length > 0
                                    }
                                    aiGuideEnabledStudentsOnly={
                                        hasAIGuideActivityContent
                                    }
                                />
                            }
                        />
                    )}
                </Id>
            )}
        </View>
    );
};

interface LabeledSelectorProps {
    labelText: string;
    selector: React.ReactNode;
    labelTooltipText?: string;
    selectorId: string;
}

const LabeledSelector = ({
    labelText,
    labelTooltipText,
    selector,
    selectorId,
}: LabeledSelectorProps) => {
    return (
        <div>
            {labelTooltipText ? (
                <View style={styles.labelWrapper}>
                    <LabelLarge htmlFor={selectorId} tag="label">
                        {labelText}
                    </LabelLarge>
                    <Strut size={spacing.xSmall_8} />
                    <Tooltip content={labelTooltipText}>
                        <PhosphorIcon
                            aria-label={labelTooltipText}
                            icon={infoIcon}
                        />
                    </Tooltip>
                </View>
            ) : (
                <LabelLarge htmlFor={selectorId} tag="label">
                    {labelText}
                </LabelLarge>
            )}
            <div className={css(styles.selector)}>{selector}</div>
        </div>
    );
};

const styles = StyleSheet.create({
    selector: {
        display: "inline-flex",
        marginBlockEnd: spacing.medium_16,
        marginBlockStart: spacing.medium_16,
    },
    labelWrapper: {
        flexDirection: "row",
        alignItems: "center",
    },
    container: {
        flexDirection: "row",

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
        },
    },
    spacer: {
        [mediaQueries.smOrSmaller]: {
            // On mobile, we stack the two "columns", and so we don't need
            // any horizontal space between them.
            display: "none",
        },
    },
});
